import type { Explanation } from '../../explanations/types';
import { commonScopes, type Translate } from '../../explanations/common';

/**
 * Os combos pré-pagos explicam seus números e ações daqui (skill
 * `self-explaining-ui`). Cada frase descreve o backend do NS 2026-0090: o razão
 * (So_combo_movements_model) decide o saldo e as recusas.
 */
export const comboExplanations = (t: Translate) => {
  const SCOPE = commonScopes(t);
  return {
    pares: {
      what: t('Quantos pares de limpeza a compra do combo dá ao cliente.'),
      now: { kind: 'info', text: t('Compras já feitas mantêm as condições da venda, mesmo que o combo mude depois.') },
    },
    validade: {
      what: t('Dias que a compra vale, contados da venda. Vazio: não vence.'),
    },
    catalogoSituacao: {
      what: t('Combo desligado sai da venda em toda a rede; quem já comprou continua usando.'),
    },
    precoUnidade: {
      what: t('Preço do combo nesta unidade, na moeda local. Vale para as próximas vendas.'),
      scope: SCOPE.localCurrency,
    },
    precoSituacao: {
      what: t('Ligado: a unidade vende o combo por este preço. Desligado: não vende, e compras já feitas continuam valendo.'),
    },
    somenteLeitura: {
      what: t('Gerente só consulta os preços. Franqueado e Full alteram.'),
    },
    semFranquia: {
      what: t('O preço do combo é de uma unidade.'),
      now: { kind: 'no', text: t('Escolha a franquia para adicionar um preço.') },
    },
    venderCombo: {
      what: t('Adiciona a venda do combo a esta OS. O saldo fica disponível na hora; o pagamento entra pelos recebimentos da OS.'),
    },
    venderSemCliente: {
      what: t('O combo fica no nome do cliente da OS.'),
      now: { kind: 'no', text: t('Esta OS não tem cliente: inicie um atendimento com cliente para vender o combo.') },
    },
    desvincular: {
      what: t('Tira o combo deste par: a reserva volta para o saldo e o item volta ao preço da matriz.'),
      now: { kind: 'info', text: t('Item já entregue não desvincula: o par foi consumido.') },
    },
    disponivel: {
      what: t('Pares que o cliente ainda pode usar nesta unidade.'),
      calculation: [t('Pares comprados'), t('− reservados em OS'), t('− consumidos'), t('+ ajustes de crédito'), t('− ajustes de débito'), t('− cancelados')],
    },
    reservado: {
      what: t('Pares vinculados a uma OS e ainda não entregues. Voltam ao saldo se o item sair da OS.'),
    },
    consumido: {
      what: t('Pares já entregues. O par só é consumido na entrega do item.'),
      calculation: [t('Consumos'), t('− estornos')],
    },
    pagamento: {
      what: t('Situação do pagamento da OS em que o combo foi vendido. O saldo não depende dela.'),
      scope: SCOPE.localCurrency,
    },
    estornar: {
      what: t('Devolve 1 par ao saldo. O consumo original continua no histórico, e um consumo só pode ser estornado uma vez.'),
    },
    ajustar: {
      what: t('Soma ou tira pares do saldo, com motivo registrado. O débito não leva o disponível abaixo de zero.'),
    },
    cancelarCompra: {
      what: t('Anula o saldo restante da compra e não tem volta. A devolução do dinheiro é feita fora do sistema.'),
      now: { kind: 'info', text: t('Com par reservado numa OS a compra não cancela: desvincule antes.') },
    },
    vendidos: {
      what: t('Compras criadas no período. A cancelada à mão continua contando, porque o dinheiro entrou; a venda desfeita com a OS não conta.'),
      scope: SCOPE.period,
    },
    valorVendido: {
      what: t('Soma do preço das compras criadas no período, canceladas à mão inclusive.'),
      scope: SCOPE.localCurrency,
    },
    cancelados: {
      what: t('Das compras vendidas no período, quantas foram canceladas depois. Já estão somadas em Vendidos.'),
      scope: SCOPE.period,
    },
    valorCancelado: {
      what: t('Valor das compras canceladas à mão, mostrado à parte. A devolução é feita fora do sistema.'),
      scope: SCOPE.localCurrency,
    },
    paresUsados: {
      what: t('Pares entregues no período, de compras de qualquer data.'),
      calculation: [t('Consumos do período'), t('− estornos do período')],
    },
    estornos: {
      what: t('Pares devolvidos ao saldo por estorno no período.'),
    },
    ajustes: {
      what: t('Pares somados ou tirados do saldo à mão no período.'),
    },
    paresCancelados: {
      what: t('Pares anulados no período pelo cancelamento de compras.'),
    },
    comprasAtivas: {
      what: t('Compras ativas da unidade agora, de qualquer data. Não depende do período.'),
    },
    saldoDisponivel: {
      what: t('Pares disponíveis agora nas compras ativas e não vencidas. Não depende do período.'),
    },
    saldoVencido: {
      what: t('Pares que sobraram em compras vencidas. Não podem mais ser usados.'),
    },
    saldoReservado: {
      what: t('Pares vinculados a OS ainda não entregues. Não depende do período.'),
    },
    precoMatriz: {
      what: t('Preço do par na matriz da unidade quando o combo cobriu o item. Referência: o item sai a 0.'),
    },
  } satisfies Record<string, Explanation>;
};

/** O rótulo de um tipo de movimento do razão; tipo novo do backend aparece cru, sem sumir. */
export function comboMovimentoLabel(t: Translate, tipo: string): string {
  const rotulos: Record<string, string> = {
    reserva: t('Reserva'),
    consumo: t('Consumo'),
    liberacao: t('Liberação'),
    estorno: t('Estorno'),
    ajuste_credito: t('Ajuste de crédito'),
    ajuste_debito: t('Ajuste de débito'),
    cancelamento: t('Cancelamento'),
  };
  return rotulos[tipo] ?? tipo;
}
